import React, { useState, useEffect } from 'react';
import api from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { getAgentRole, canExport } from '../lib/roles';


function isLeadershipOrAdmin(role) {
  return role === 'leadership' || role === 'admin';
}

export function TopRequests() {
  const [period, setPeriod] = useState('all_time');
  const [topClusters, setTopClusters] = useState([]);
  const [excludedClusters, setExcludedClusters] = useState([]);
  const [showExcluded, setShowExcluded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [excludingId, setExcludingId] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludeCustomReason, setExcludeCustomReason] = useState('');
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [pendingExcludeId, setPendingExcludeId] = useState(null);

  const role = getAgentRole();
  const canModerate = isLeadershipOrAdmin(role);

  useEffect(() => {
    fetchRankings();
  }, [period, showExcluded]);

  const fetchRankings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.rankings({
        period,
        include_excluded: showExcluded ? 'true' : 'false',
      });

      if (response.success) {
        setTopClusters(response.top_clusters || []);
        setExcludedClusters(response.excluded_clusters || []);
      } else {
        setError(response.error?.message || 'Failed to load rankings');
      }
    } catch (err) {
      setError(err.message || 'Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleExcludeClick = (clusterId) => {
    setPendingExcludeId(clusterId);
    setExcludeReason('');
    setExcludeCustomReason('');
    setShowExcludeModal(true);
  };

  const handleExcludeConfirm = async () => {
    if (!pendingExcludeId) return;

    const reason = excludeReason === 'custom' ? excludeCustomReason : excludeReason;
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    setExcludingId(pendingExcludeId);
    try {
      const response = await api.excludeCluster(pendingExcludeId, reason);
      if (response.success) {
        setShowExcludeModal(false);
        fetchRankings();
      } else {
        setError(response.error?.message || 'Failed to exclude cluster');
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to exclude clusters');
      } else {
        setError(err.message || 'Failed to exclude cluster');
      }
    } finally {
      setExcludingId(null);
      setPendingExcludeId(null);
    }
  };

  const handleRestore = async (clusterId) => {
    setExcludingId(clusterId);
    try {
      const response = await api.unexcludeCluster(clusterId);
      if (response.success) {
        fetchRankings();
      } else {
        setError(response.error?.message || 'Failed to restore cluster');
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to restore clusters');
      } else {
        setError(err.message || 'Failed to restore cluster');
      }
    } finally {
      setExcludingId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 className="text-2xl font-bold text-coros-text-primary mb-2">Top Requests</h1>
        <p className="text-coros-text-muted">Ranked features most requested by customers</p>
      </div>

      {/* Period Selector & Toggles */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
        marginBottom: 32,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setPeriod('all_time')}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              border: period === 'all_time' ? '2px solid var(--coros-red)' : '1px solid var(--border-color)',
              background: period === 'all_time' ? 'rgba(229, 46, 58, 0.1)' : 'var(--bg-secondary)',
              color: period === 'all_time' ? 'var(--coros-red)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            All Time
          </button>
          <button
            onClick={() => setPeriod('last_90_days')}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              border: period === 'last_90_days' ? '2px solid var(--coros-red)' : '1px solid var(--border-color)',
              background: period === 'last_90_days' ? 'rgba(229, 46, 58, 0.1)' : 'var(--bg-secondary)',
              color: period === 'last_90_days' ? 'var(--coros-red)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            Last 90 Days
          </button>
        </div>

        {canModerate && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={showExcluded}
              onChange={(e) => setShowExcluded(e.target.checked)}
              style={{ cursor: 'pointer', width: 16, height: 16 }}
            />
            Show excluded
          </label>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: 24,
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: 8,
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: 'var(--color-error)',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">Loading rankings...</p>
        </div>
      )}

      {/* Top Clusters List */}
      {!loading && topClusters.length > 0 && (
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {topClusters.map((cluster) => (
              <div
                key={cluster.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '16px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 8,
                  border: '1px solid var(--border-color)',
                  transition: 'background 0.15s',
                }}
              >
                {/* Rank */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  background: 'var(--bg-tertiary)',
                  borderRadius: 6,
                  flexShrink: 0,
                }}>
                  <span style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'var(--coros-red)',
                  }}>
                    {cluster.rank}
                  </span>
                </div>

                {/* Cluster info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    margin: '0 0 4px 0',
                  }}>
                    {cluster.name}
                  </h3>
                  {cluster.description && (
                    <p style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      margin: '0 0 8px 0',
                      lineHeight: 1.4,
                    }}>
                      {cluster.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}>
                      {cluster.count} requests
                    </span>
                    {cluster.status && (
                      <div>
                        <StatusBadge status={cluster.status} size="xs" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Exclude button */}
                {canModerate && (
                  <button
                    onClick={() => handleExcludeClick(cluster.id)}
                    disabled={excludingId === cluster.id}
                    style={{
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-error)',
                      background: 'transparent',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.15s',
                      flexShrink: 0,
                      opacity: excludingId === cluster.id ? 0.5 : 1,
                    }}
                  >
                    {excludingId === cluster.id ? 'Excluding...' : 'Exclude'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Excluded Clusters */}
      {showExcluded && excludedClusters.length > 0 && (
        <div>
          <h2 style={{
            fontSize: 15,
            fontWeight: 700,
            color: 'var(--text-muted)',
            marginBottom: 16,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Excluded Clusters
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {excludedClusters.map((cluster) => (
              <div
                key={cluster.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '16px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 8,
                  border: '1px solid var(--border-color)',
                  opacity: 0.6,
                }}
              >
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    margin: '0 0 4px 0',
                  }}>
                    {cluster.name}
                  </h3>
                  <div style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    marginBottom: 8,
                  }}>
                    <span style={{ fontWeight: 500 }}>{cluster.count} requests</span>
                    {cluster.excluded_reason && (
                      <span> • Reason: {cluster.excluded_reason}</span>
                    )}
                  </div>
                </div>

                {/* Restore button */}
                {canModerate && (
                  <button
                    onClick={() => handleRestore(cluster.id)}
                    disabled={excludingId === cluster.id}
                    style={{
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      background: 'transparent',
                      border: '1px solid var(--border-color)',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.15s',
                      flexShrink: 0,
                      opacity: excludingId === cluster.id ? 0.5 : 1,
                    }}
                  >
                    {excludingId === cluster.id ? 'Restoring...' : 'Restore'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && topClusters.length === 0 && !error && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            No clusters found for this period
          </p>
        </div>
      )}

      {/* Exclude Modal */}
      {showExcludeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px',
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: 12,
            border: '1px solid var(--border-color)',
            padding: '24px',
            maxWidth: 400,
            width: '100%',
          }}>
            <h2 style={{
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: 16,
            }}>
              Exclude this cluster?
            </h2>

            <div style={{ marginBottom: 20 }}>
              <p style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: 8,
              }}>
                Reason for exclusion
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Shipped', 'Impossible', 'Duplicate'].map((r) => (
                  <label key={r} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}>
                    <input
                      type="radio"
                      name="reason"
                      value={r}
                      checked={excludeReason === r}
                      onChange={(e) => setExcludeReason(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    {r}
                  </label>
                ))}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                }}>
                  <input
                    type="radio"
                    name="reason"
                    value="custom"
                    checked={excludeReason === 'custom'}
                    onChange={(e) => setExcludeReason(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  Custom reason
                </label>
              </div>

              {excludeReason === 'custom' && (
                <textarea
                  value={excludeCustomReason}
                  onChange={(e) => setExcludeCustomReason(e.target.value)}
                  placeholder="Explain why this cluster is being excluded..."
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    marginTop: 8,
                    minHeight: 60,
                  }}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowExcludeModal(false);
                  setPendingExcludeId(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleExcludeConfirm}
                style={{
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--color-error)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Exclude
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopRequests;
